import React, { useState, useEffect } from 'react';
import api from '../services/api';
import {
    Calendar as CalendarIcon,
    Clock,
    Info,
    QrCode,
    CheckCircle2,
    ArrowRight,
    History,
    Search,
    MapPin,
    Users
} from 'lucide-react';
import { cn } from '../lib/utils';
import { StatusBadge } from '../components/ui/StatusBadge';
import { uploadFile } from '../services/upload';

const FacilityBooking: React.FC<{ activeUnitId: string }> = ({ activeUnitId }) => {
    const [facilities, setFacilities] = useState<any[]>([]);
    const [selectedFacility, setSelectedFacility] = useState<any>(null);
    const [date, setDate] = useState('');
    const [utr, setUtr] = useState('');
    const [proofFile, setProofFile] = useState<File | null>(null);
    const [uploading, setUploading] = useState(false);
    const [view, setView] = useState<'browse' | 'history'>('browse');
    const [myBookings, setMyBookings] = useState<any[]>([]);
    const [availability, setAvailability] = useState<any[]>([]);

    useEffect(() => {
        api.get('/facilities').then(res => setFacilities(res.data));
        fetchMyBookings();
    }, []);

    useEffect(() => {
        if (selectedFacility && date) {
            api.get(`/bookings/availability?facility_id=${selectedFacility.id}&date=${date}`)
               .then(res => setAvailability(res.data));
        }
    }, [selectedFacility, date]);

    const fetchMyBookings = async () => {
        const res = await api.get('/bookings/me');
        setMyBookings(res.data);
    };

    const handleBooking = async () => {
        if (!date || !selectedFacility) return;
        if (!proofFile) {
            alert('Please upload a PDF or image of the transaction proof.');
            return;
        }
        setUploading(true);
        try {
            // 1. Upload proof
            const proofUrl = await uploadFile(proofFile, 'bookings');
            
            // 2. Request booking
            await api.post('/bookings', {
                facility_id: selectedFacility.id,
                booking_date: date,
                unit_id: activeUnitId,
                payment_ref: utr || null,
                payment_screenshot_url: proofUrl
            });
            alert('Booking request submitted! Please wait for admin approval.');
            setDate('');
            setUtr('');
            setProofFile(null);
            setView('history');
            fetchMyBookings();
        } catch (e) { 
            alert('Booking failed'); 
        } finally {
            setUploading(false);
        }
    };

    return (
        <div className="space-y-8 max-w-4xl mx-auto">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                    <h1 className="text-3xl font-bold text-slate-900">Facility Booking</h1>
                    <p className="text-slate-500 mt-1">Reserve common areas and amenities</p>
                </div>
                <div className="flex bg-slate-100 p-1 rounded-lg">
                    <button
                        onClick={() => setView('browse')}
                        className={cn("px-4 py-1.5 rounded-md text-xs font-bold uppercase transition-all", view === 'browse' ? "bg-white text-slate-900 shadow-sm" : "text-slate-500")}
                    >
                        Browse
                    </button>
                    <button
                        onClick={() => setView('history')}
                        className={cn("px-4 py-1.5 rounded-md text-xs font-bold uppercase transition-all", view === 'history' ? "bg-white text-slate-900 shadow-sm" : "text-slate-500")}
                    >
                        My Bookings
                    </button>
                </div>
            </div>

            {view === 'browse' ? (
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                    <div className="lg:col-span-2 space-y-4">
                        {facilities.map((f) => (
                            <div
                                key={f.id}
                                onClick={() => setSelectedFacility(f)}
                                className={cn(
                                    "p-5 rounded-2xl border transition-all cursor-pointer group flex gap-5",
                                    selectedFacility?.id === f.id ? "bg-white border-violet-600 shadow-md" : "bg-white border-slate-200 hover:border-violet-300"
                                )}
                            >
                                <div className="w-20 h-20 bg-slate-100 rounded-xl flex items-center justify-center text-slate-400 group-hover:text-violet-600 transition-colors shrink-0">
                                    <CalendarIcon size={32} />
                                </div>
                                <div className="flex-1">
                                    <h3 className="text-lg font-bold text-slate-900">{f.name}</h3>
                                    <p className="text-sm text-slate-500 line-clamp-2 mt-1">{f.description}</p>
                                    <div className="mt-3 flex items-center gap-4 text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                                        <div className="flex items-center gap-1"><Users size={12} /> Max {f.max_capacity || 10}</div>
                                        <div className="flex items-center gap-1 font-bold text-violet-600">₹{f.hourly_rate || 0}/hr</div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    <div className="space-y-6">
                        {selectedFacility ? (
                            <div className="bg-white rounded-2xl p-6 border border-slate-200 shadow-sm sticky top-24">
                                <h2 className="text-xl font-bold text-slate-900 mb-6">Book {selectedFacility.name}</h2>

                                <div className="space-y-4">
                                    <div>
                                        <label className="text-xs font-bold text-slate-500 uppercase mb-1.5 block">Select Date</label>
                                        <input
                                            type="date"
                                            className="w-full bg-slate-50 border border-slate-200 rounded-xl p-3 text-sm focus:ring-2 focus:ring-violet-500 outline-none"
                                            value={date}
                                            onChange={e => setDate(e.target.value)}
                                        />
                                    </div>

                                    {date && (
                                        <div className="p-4 bg-slate-50 rounded-xl border border-slate-100">
                                            <p className="text-[10px] font-bold text-slate-400 uppercase mb-2">Booked Slots</p>
                                            {availability.length > 0 ? (
                                                <div className="space-y-1">
                                                    {availability.map((a, i) => (
                                                        <div key={i} className="text-xs font-medium text-slate-600 bg-white p-2 rounded border border-slate-100 flex justify-between">
                                                            <span>{new Date(a.start).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                                                            <span className="text-red-500 font-bold uppercase text-[8px]">Occupied</span>
                                                        </div>
                                                    ))}
                                                </div>
                                            ) : (
                                                <p className="text-xs text-emerald-600 font-medium">All slots available</p>
                                            )}
                                        </div>
                                    )}

                                    <div className="p-4 bg-violet-50 rounded-xl border border-violet-100 space-y-3">
                                        <div className="flex gap-3 mb-1">
                                            <QrCode className="text-violet-600" size={20} />
                                            <p className="text-xs font-bold text-violet-900">Payment Instructions</p>
                                        </div>
                                        <p className="text-[10px] text-violet-700 leading-relaxed mb-1">
                                            Scan society QR code at the reception or use UPI ID: <span className="font-bold">society@upi</span>.
                                        </p>
                                        
                                        <div>
                                            <label className="text-[10px] font-bold text-slate-500 uppercase mb-1 block">Upload Transaction Proof (PDF/Image) *</label>
                                            <input
                                                type="file"
                                                accept="image/*,application/pdf"
                                                className="w-full bg-white border border-violet-200 rounded-lg p-2 text-xs outline-none"
                                                onChange={e => {
                                                    const file = e.target.files?.[0];
                                                    if (file) setProofFile(file);
                                                }}
                                            />
                                        </div>

                                        <div>
                                            <label className="text-[10px] font-bold text-slate-500 uppercase mb-1 block">UTR / Transaction Reference (Optional)</label>
                                            <input
                                                type="text"
                                                placeholder="Enter 12-digit UTR (Optional)"
                                                className="w-full bg-white border border-violet-200 rounded-lg p-2.5 text-sm focus:ring-2 focus:ring-violet-500 outline-none"
                                                value={utr}
                                                onChange={e => setUtr(e.target.value)}
                                            />
                                        </div>
                                    </div>

                                    <button
                                        onClick={handleBooking}
                                        disabled={uploading}
                                        className={cn(
                                            "w-full bg-violet-600 hover:bg-violet-700 text-white font-bold py-3.5 rounded-xl transition-all shadow-lg shadow-violet-200 flex items-center justify-center gap-2",
                                            uploading && "opacity-70 cursor-not-allowed"
                                        )}
                                    >
                                        {uploading ? 'Submitting...' : 'Request Booking'} <ArrowRight size={18} />
                                    </button>
                                </div>
                            </div>
                        ) : (
                            <div className="bg-slate-50 rounded-2xl border-2 border-dashed border-slate-200 p-8 text-center flex flex-col items-center justify-center min-h-[300px]">
                                <CalendarIcon size={32} className="text-slate-300 mb-2" />
                                <p className="text-sm text-slate-400 font-medium">Select a facility to start</p>
                            </div>
                        )}
                    </div>
                </div>
            ) : (
                <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                    <table className="w-full text-sm">
                        <thead className="bg-slate-50 border-b border-slate-200">
                            <tr>
                                <th className="px-6 py-4 text-left font-bold text-slate-500 uppercase text-[10px]">Facility</th>
                                <th className="px-6 py-4 text-left font-bold text-slate-500 uppercase text-[10px]">Date</th>
                                <th className="px-6 py-4 text-left font-bold text-slate-500 uppercase text-[10px]">Reference</th>
                                <th className="px-6 py-4 text-right font-bold text-slate-500 uppercase text-[10px]">Status</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                            {myBookings.map((b) => (
                                <tr key={b.id} className="hover:bg-slate-50/50 transition-colors">
                                    <td className="px-6 py-4 font-bold text-slate-900">
                                        {facilities.find(f => f.id === b.facility_id)?.name || 'Facility'}
                                    </td>
                                    <td className="px-6 py-4 text-slate-600">
                                        {new Date(b.booking_date).toLocaleDateString()}
                                    </td>
                                    <td className="px-6 py-4 font-mono text-xs text-slate-400">{b.payment_ref || 'N/A'}</td>
                                    <td className="px-6 py-4 text-right">
                                        <StatusBadge status={b.status} />
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    {myBookings.length === 0 && (
                        <div className="py-12 text-center text-slate-400 font-medium italic">No booking history</div>
                    )}
                </div>
            )}
        </div>
    );
};

export default FacilityBooking;
